import type { AqFact, AqItem } from '@elva/ui';
import { growthGet, growthList, growthPost } from '@/data/http';
import type { BeApprovalItem, BeApprovalSummary } from '@/data/marketingApiTypes';
import { centsToUsd, enumLower, minutesAgoFromIso, num, str } from '@/data/adapt';
import {
  approvalFeTabToBeUiBucket,
  approvalItemTypeToUiBucket,
  approvalPriorityToFe,
  approvalUiBucketToFe,
  approvalUiBucketToFeOrNull,
} from '@/data/growthEnums';

export interface ApprovalsData {
  items: AqItem[];
  tabCounts: Record<string, number>;
}

const APPROVAL_TAB_IDS = [
  'all',
  'campaign',
  'article',
  'landing-page',
  'gbp-post',
  'review-reply',
  'seo-fix',
] as const;

function emptyApprovalTabCounts(): Record<string, number> {
  return Object.fromEntries(APPROVAL_TAB_IDS.map((id) => [id, 0]));
}

function adaptApprovalSummary(summary: BeApprovalSummary): Record<string, number> {
  const counts = emptyApprovalTabCounts();
  const byBucket = summary.ui_buckets ?? summary.by_bucket ?? summary.counts ?? {};
  for (const [key, value] of Object.entries(byBucket)) {
    const feKey = approvalUiBucketToFeOrNull(key);
    if (!feKey) continue;
    counts[feKey] = num(counts[feKey]) + num(value);
  }
  if (summary.total_waiting != null) counts.all = num(summary.total_waiting);
  else if (summary.total != null) counts.all = num(summary.total);
  return counts;
}

function formatFactValue(label: string, value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (/cents/i.test(label)) return centsToUsd(value);
    return value.toLocaleString();
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function adaptLegacyFacts(payload: BeApprovalItem['display_payload']): AqFact[] {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.map((f) => ({
      label: str(f.label),
      value: formatFactValue(str(f.label), f.value),
    }));
  }
  return Object.entries(payload).map(([label, value]) => ({
    label,
    value: formatFactValue(label, value),
  }));
}

function adaptFacts(row: BeApprovalItem): AqFact[] {
  if (Array.isArray(row.facts) && row.facts.length > 0) {
    return row.facts.map((f) => ({
      label: str(f.label),
      value: formatFactValue(str(f.label), f.value),
    }));
  }
  return adaptLegacyFacts(row.display_payload);
}

export function adaptApprovalItem(row: BeApprovalItem): AqItem {
  const type = approvalUiBucketToFe(
    row.ui_bucket ?? row.type ?? approvalItemTypeToUiBucket(row.item_type),
  );
  const minutesAgo =
    row.minutes_ago != null ? num(row.minutes_ago) : minutesAgoFromIso(row.created_at);
  return {
    id: str(row.id),
    type,
    title: str(row.title),
    summary: str(row.summary ?? row.subtitle),
    minutesAgo,
    createdBy: str(row.created_by, 'ELVA'),
    priority: approvalPriorityToFe(row.priority),
    reason: str(row.reason ?? row.ui_rationale, '—'),
    facts: adaptFacts(row),
    reviewTo: str(row.review_to, '#'),
  };
}

function isPendingApproval(row: BeApprovalItem): boolean {
  return enumLower(row.status, 'pending') === 'pending';
}

export async function fetchApprovals(feTab?: string): Promise<ApprovalsData> {
  const params: Record<string, string | number> = {
    page: 1,
    limit: 100,
  };
  const beBucket = approvalFeTabToBeUiBucket(feTab);
  if (beBucket) params.ui_bucket = beBucket;

  const [listResult, summaryResult] = await Promise.allSettled([
    growthList<BeApprovalItem>('/approval-queue/', params),
    growthGet<BeApprovalSummary>('/approval-queue/summary/'),
  ]);

  const items =
    listResult.status === 'fulfilled'
      ? listResult.value.items.filter(isPendingApproval).map(adaptApprovalItem)
      : [];

  const tabCounts =
    summaryResult.status === 'fulfilled'
      ? adaptApprovalSummary(summaryResult.value)
      : emptyApprovalTabCounts();

  if (listResult.status === 'rejected') throw listResult.reason;

  return { items, tabCounts };
}

export async function approveItem(id: string) {
  return growthPost(`/approval-queue/${id}/approve/`);
}

export async function rejectItem(id: string, reason?: string) {
  return growthPost(`/approval-queue/${id}/reject/`, reason ? { reason } : {});
}

export async function bulkApprove(ids: string[]) {
  return growthPost('/approval-queue/bulk-approve/', { item_ids: ids });
}
